import { useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import { $notificationStore, $walletAddressesStore } from '@lib/store';
import { cn, CryptoUtils, PaletteUtils } from '@shared/utils';
import { Button, Icon, Modal, Text } from '@shared/widgets';
import ChevronIcon from '@assets/icons/chevron.svg';
import CopyIcon from '@assets/icons/copy.svg';
import AlertIcon from '@assets/icons/alert.svg';

interface TopUpWithCryptoModalProps {
  isOpen: boolean;
  onClose: () => void;
  accountId: number;
}

export const TopUpWithCryptoModal = (props: TopUpWithCryptoModalProps) => {
  const walletAddressesStore = useUnit($walletAddressesStore.store);

  const [expandedId, setExpandedId] = useState<number | null>(null);

  const { t } = useTranslation('accounts');

  const closeHandler = () => {
    setExpandedId(null);
    props.onClose();
  };
  const toggleHandler = (index: number) => setExpandedId((prev) => (prev === index ? null : index));
  const copyHandler = async (address: string) => {
    try {
      await navigator.clipboard.writeText(address);
      $notificationStore.events.setSuccess('Copied to clipboard');
    } catch {
      $notificationStore.events.setError('Failed to copy to clipboard');
    }
  };

  return (
    <Modal isOpen={props.isOpen} onClose={closeHandler} title={t('modals.topUpWithCrypto.title')} width='500px'>
      <div className='flex flex-col gap-3'>
        {walletAddressesStore.data.map((walletAddress, idx) => (
          <div
            key={idx}
            className={cn(
              'overflow-hidden rounded-xl border',
              PaletteUtils.isInsigniaPalette && 'border-light bg-ice dark:border-gray-black dark:bg-soft-black',
              PaletteUtils.isVcfcPalette && 'border-gray-black bg-ghost-white dark:bg-light/5',
            )}
          >
            <button
              type='button'
              className='flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left'
              aria-expanded={expandedId === idx}
              onClick={() => toggleHandler(idx)}
            >
              <Icon
                className={cn('flex size-12 items-center justify-center', walletAddress.coin.toLowerCase() === 'eth' && 'size-6')}
                icon={CryptoUtils.getCryptoIcon(walletAddress.coin)}
              />
              <ul className='flex flex-1 flex-col gap-1'>
                <Text tag='li' weight='semibold' leading='none' size='lg'>
                  {walletAddress.coin}
                </Text>
                <Text tag='li' size='sm' leading='none' color='secondary'>
                  {CryptoUtils.getCryptoNetworkName(walletAddress.network)}
                </Text>
              </ul>
              <Icon
                className={cn('-rotate-90 transition-transform duration-300 ease-in-out', expandedId === idx && 'rotate-90')}
                icon={<ChevronIcon />}
              />
            </button>
            <div
              className={cn(
                'grid transition-all duration-300 ease-in-out',
                expandedId === idx ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
              )}
            >
              <div className='overflow-hidden'>
                <div className='flex flex-col items-center gap-4 px-4 pb-3'>
                  <div className='rounded-lg bg-white p-3'>
                    <QRCode size={140} value={CryptoUtils.getCryptoQrValue(walletAddress.network, walletAddress.address)} />
                  </div>
                  <div className='flex items-center gap-1 rounded-full bg-dark-red-sand px-3 py-1.5'>
                    <Text nowrap weight='bold' size='sm'>
                      {walletAddress.address}
                    </Text>
                    <Button className='p-1' color='icon' onClick={(e) => (e.stopPropagation(), copyHandler(walletAddress.address))}>
                      <Icon icon={<CopyIcon width={14} height={14} />} />
                    </Button>
                  </div>
                  <div className='flex gap-2 rounded-lg bg-[#F4F5D8] p-3 dark:bg-olive-gray'>
                    <Icon className='flex-none text-yellow-ocher dark:text-ecru' icon={<AlertIcon width={25} height={25} />} />
                    <div className='flex flex-col gap-1'>
                      <Text weight='semibold'>
                        {t('modals.topUpWithCrypto.warning.title', {
                          coin: walletAddress.coin,
                          network: CryptoUtils.getCryptoNetworkName(walletAddress.network),
                        })}
                      </Text>
                      <Text size='xs' color='secondary'>
                        {t('modals.topUpWithCrypto.warning.description', {
                          coin: walletAddress.coin,
                          network: CryptoUtils.getCryptoNetworkName(walletAddress.network),
                        })}
                      </Text>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
};
